/**
 * Reading-time lint: flags on-screen text held too briefly to read. Ports codeo's read-time math
 * (~15 chars/sec, 1.2s floor, ±0.05s tolerance) but *measures* visibility — seeking the timeline and
 * reading rendered opacity — rather than trusting author-declared attributes, which HtmlAsset
 * compositions don't have. The measured progress-span is converted to seconds via the scene's real
 * playback duration.
 */
import { effectiveOpacity } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

// A perceptual floor plus a ~15 chars/sec reading rate.
const MIN_READ_TIME_S = 1.2;
const READING_CHARS_PER_SEC = 15;

// Slack so a borderline window doesn't flip-flop between runs.
export const READING_TIME_TOLERANCE_S = 0.05;

/** Seconds a viewer needs to comfortably read `chars` characters. */
export function requiredReadTimeS(chars: number): number {
    return Math.max(MIN_READ_TIME_S, 0.4 + chars / READING_CHARS_PER_SEC);
}

/** Whether text of `chars` shown for `visibleSeconds` is too brief to read (within tolerance). */
export function readsTooFast(visibleSeconds: number, chars: number): boolean {
    return visibleSeconds + READING_TIME_TOLERANCE_S < requiredReadTimeS(chars);
}

/**
 * Longest contiguous visible span in progress units (0..1) — the first-to-last progress of the run.
 * Multiple appearances yield the longest run; a single-sample flash and empty input yield 0.
 */
export function longestVisibleSpanProgress(
    samples: ReadonlyArray<{ progress: number; visible: boolean }>,
): number {
    let longest = 0;
    let runStart: number | null = null;
    for (const { progress, visible } of samples) {
        if (visible) {
            if (runStart === null) {
                runStart = progress;
            }
            longest = Math.max(longest, progress - runStart);
        } else {
            runStart = null;
        }
    }
    return longest;
}

// ~24 evenly-spaced seeks: enough to resolve a ~0.25s flash without a costly number of seeks.
const SAMPLE_COUNT = 24;
// Below this opacity, a fade's dim frames don't count as legible read time.
const VISIBLE_OPACITY = 0.5;
const TEXT_NODE = 3;
const NON_RENDERED_TAGS = new Set([
    'SCRIPT',
    'STYLE',
    'TEMPLATE',
    'NOSCRIPT',
    'TITLE',
    'META',
    'LINK',
]);

interface TextLeaf {
    element: Element;
    chars: number;
    preview: string;
}

// The element's own direct text only (descendant text belongs to descendant leaves), whitespace-
// collapsed — the readable copy this element itself contributes.
function ownRenderedText(element: Element): string {
    return Array.from(element.childNodes)
        .filter((node) => node.nodeType === TEXT_NODE)
        .map((node) => node.textContent ?? '')
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

// Elements rendering their own copy, minus ambient text (watermarks/motifs), which isn't read.
function collectTextLeaves(document: Document): TextLeaf[] {
    const body = document.body;
    if (!body) {
        return [];
    }
    const leaves: TextLeaf[] = [];
    for (const element of Array.from(body.querySelectorAll('*'))) {
        if (NON_RENDERED_TAGS.has(element.tagName) || element.tagName === 'BR') {
            continue;
        }
        if (element.closest('[data-l5-text-role="ambient"]')) {
            continue;
        }
        const text = ownRenderedText(element);
        if (!text) {
            continue;
        }
        leaves.push({ element, chars: text.length, preview: text });
    }
    return leaves;
}

interface BoxLike {
    left: number;
    right: number;
    top: number;
    bottom: number;
    width: number;
    height: number;
}

// A zero-area box (mid-layout, or jsdom's no-layout rects) gets the benefit of the doubt, so this only
// excludes text genuinely animated off-screen.
function intersectsCanvas(
    box: BoxLike,
    canvas: { width: number; height: number } | undefined,
): boolean {
    if (!canvas) {
        return true;
    }
    if (box.width <= 0 || box.height <= 0) {
        return true;
    }
    return box.left < canvas.width && box.right > 0 && box.top < canvas.height && box.bottom > 0;
}

/**
 * Flags text whose longest legible span is shorter than its read time, via a dense seek-and-measure
 * pass converted to seconds with the real `sceneDurationMs`. Warning severity. Returns [] when it
 * can't judge honestly: no real duration, no seekable timeline, or no text.
 */
export const textReadingTime: RuntimeLintRule = {
    id: 'text-reading-time',
    severity: 'warning',
    guidance:
        'Hold readable text on screen long enough to actually read (≈15 chars/sec, min ~1.2s). If a ' +
        'piece is shown too briefly: when the scene’s duration is flexible (text-on-media) lengthen ' +
        'the scene; when it is fixed by a voiceover, simplify the design — fewer words, larger, or ' +
        'sequence pieces so each holds longer. Mark genuinely-ambient text (watermarks, kinetic ' +
        'background motifs) `data-l5-text-role="ambient"` to waive this.',
    async check(context: RuntimeLintContext): Promise<string[]> {
        const { timeline, sceneDurationMs, canvasSize } = context;
        if (!timeline) {
            return [];
        }
        if (
            typeof sceneDurationMs !== 'number' ||
            !Number.isFinite(sceneDurationMs) ||
            sceneDurationMs <= 0
        ) {
            return [];
        }
        const sceneDurationSeconds = sceneDurationMs / 1000;
        const duration = timeline.duration();
        if (!(duration > 0)) {
            return [];
        }
        const leaves = collectTextLeaves(context.document);
        if (leaves.length === 0) {
            return [];
        }

        const samplesByLeaf: Array<Array<{ progress: number; visible: boolean }>> = leaves.map(
            () => [],
        );
        const originalTime = timeline.time();
        try {
            for (let i = 0; i < SAMPLE_COUNT; i++) {
                const progress = i / (SAMPLE_COUNT - 1);
                timeline.seek(progress * duration);
                leaves.forEach((leaf, index) => {
                    const visible =
                        effectiveOpacity(leaf.element) >= VISIBLE_OPACITY &&
                        intersectsCanvas(leaf.element.getBoundingClientRect(), canvasSize);
                    samplesByLeaf[index].push({ progress, visible });
                });
            }
        } finally {
            timeline.seek(originalTime);
        }

        // A run undercounts the true window by up to half a sample interval each side (visibility
        // starts/ends between samples). Add one interval back so a genuinely-long-enough window isn't
        // flagged — biasing the heuristic away from false positives.
        const sampleInterval = 1 / (SAMPLE_COUNT - 1);
        const messages: string[] = [];
        leaves.forEach((leaf, index) => {
            const samples = samplesByLeaf[index];
            const rawSpan = longestVisibleSpanProgress(samples);
            const spanProgress = samples.some((sample) => sample.visible)
                ? Math.min(1, rawSpan + sampleInterval)
                : 0;
            const spanSeconds = spanProgress * sceneDurationSeconds;
            if (!readsTooFast(spanSeconds, leaf.chars)) {
                return;
            }
            const preview =
                leaf.preview.length > 40 ? `${leaf.preview.slice(0, 37)}…` : leaf.preview;
            messages.push(
                `"${preview}" (${leaf.chars} chars) is on screen for only ~${spanSeconds.toFixed(2)}s ` +
                    `but needs ~${requiredReadTimeS(leaf.chars).toFixed(2)}s to read comfortably. Hold ` +
                    `it longer (lengthen the scene if its duration is flexible, or sequence it so it ` +
                    `stays up longer), shorten the copy, or mark it data-l5-text-role="ambient" if it ` +
                    `is decorative and not meant to be read.`,
            );
        });
        return messages;
    },
};
